import { isValidElement, useId } from 'react';
import { Controller } from 'react-hook-form';
import { cn } from '@skupreme/shared/ui';
import {
  CurrencyMaskInput,
  MaskInput,
  NumberMaskInput,
  PercentageMaskInput,
} from '../CustomMaskInput';
import type {
  MaskedNumberFormFieldProps,
  MaskedNumberUncontrolledFormFieldProps,
  MaskedPercentageFormFieldProps,
} from 'types/common/form-fields';
import {
  CustomField,
  FieldLabel,
  FieldMessage,
  InputContainer,
  MuteLabel,
  RequiredMark,
  FormHelpToolTip,
} from './chrome';
import { FIELD_INPUT_INVALID } from './styles';

/*
 * These fields used to spread their leftover props over both the container
 * and the input. The container keeps only what lays it out, so handlers such
 * as `onFocus` and `onBlur` fire once, from the input.
 */
function containerProps(rest: { className?: string; style?: object }) {
  return { className: rest.className, style: rest.style };
}

export function MaskNumberUncontrolledFormField({
  name,
  value,
  setValue,
  required,
  error,
  label,
  hint,
  width,
  placeholder,
  inputRight,
  inputLeft,
  maskOptions,
  fieldClass,
  inline,
  helpText,
  helpPosition,
  labelDescription,
  ...rest
}: MaskedNumberUncontrolledFormFieldProps) {
  const id = useId();
  return (
    <CustomField width={width} className={fieldClass} inline={inline || false}>
      {label && (
        <MuteLabel
          htmlFor={id}
          className={cn(
            'sk:mb-1 sk:flex! sk:items-center',
            labelDescription && 'has-description',
          )}
        >
          {label}
          {required ? <RequiredMark /> : null}
          <FormHelpToolTip helpText={helpText} helpPosition={helpPosition} />
        </MuteLabel>
      )}
      {labelDescription && (
        <div className="sk:mb-2 sk:text-dense sk:text-fg-tertiary sk:md:max-xl:min-h-15.5 sk:xl:min-h-10.5">
          {labelDescription}
        </div>
      )}
      <InputContainer inline={inline} {...containerProps(rest)}>
        {inputLeft && inputLeft}
        <NumberMaskInput
          id={id}
          inputLeft={!!inputLeft}
          value={value}
          data-field-value={name}
          aria-invalid={!!error || undefined}
          onChange={newValue => setValue(newValue)}
          maskOptions={maskOptions}
          placeholder={placeholder || (isValidElement(label) ? '' : label)}
          {...rest}
          className={cn(!!error && FIELD_INPUT_INVALID, rest.className)}
        />
        {inputRight && inputRight}
      </InputContainer>
      {hint && <FieldMessage>{hint}</FieldMessage>}
    </CustomField>
  );
}

export function MaskedNumberFormField({
  defaultValue = null,
  name,
  control,
  inputLeft,
  ...rest
}: MaskedNumberFormFieldProps) {
  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field: { onChange, value } }) => (
        <MaskNumberUncontrolledFormField
          inputLeft={inputLeft}
          value={value}
          setValue={next => onChange(next)}
          name={name}
          {...rest}
        />
      )}
    />
  );
}

export function MaskedCurrencyFormField({
  name,
  control,
  required,
  error,
  label,
  width,
  placeholder,
  inputRight,
  inputLeft,
  maskOptions,
  fieldClass,
  hint,
  defaultValue = null,
  ...rest
}) {
  const id = useId();
  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field: { onChange, value } }) => (
        <CustomField width={width} className={fieldClass}>
          <FieldLabel label={label} required={required} htmlFor={id} />
          <InputContainer>
            {inputLeft && inputLeft}
            <CurrencyMaskInput
              id={id}
              value={value}
              data-field-value={name}
              aria-invalid={!!error || undefined}
              onChange={next => onChange(next)}
              maskOptions={maskOptions}
              placeholder={placeholder || (isValidElement(label) ? '' : label)}
              {...rest}
              className={cn(!!error && FIELD_INPUT_INVALID, rest.className)}
            />
            {inputRight && inputRight}
          </InputContainer>
          {hint && <FieldMessage>{hint}</FieldMessage>}
        </CustomField>
      )}
    />
  );
}

export function MaskedFormField({
  defaultValue,
  name,
  control,
  required,
  error,
  label,
  width,
  placeholder,
  inputRight,
  mask,
  fieldClass,
  hint,
  ...rest
}) {
  const id = useId();
  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field: { onChange, value } }) => (
        <CustomField width={width} className={fieldClass}>
          <FieldLabel label={label} required={required} htmlFor={id} />
          <InputContainer>
            <MaskInput
              id={id}
              value={value}
              data-field-value={name}
              aria-invalid={!!error || undefined}
              onChange={next => onChange(next)}
              mask={mask}
              placeholder={placeholder || (isValidElement(label) ? '' : label)}
              {...rest}
              className={cn(!!error && FIELD_INPUT_INVALID, rest.className)}
            />
            {inputRight}
          </InputContainer>
          {hint && <FieldMessage>{hint}</FieldMessage>}
        </CustomField>
      )}
    />
  );
}

export function MaskedPercentageFormField({
  defaultValue,
  name,
  control,
  required,
  error,
  label,
  width,
  placeholder,
  inputRight,
  maskOptions,
  fieldClass,
  inputContainerClass,
  inline,
  ...rest
}: MaskedPercentageFormFieldProps) {
  const id = useId();
  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field: { onChange, value } }) => (
        <CustomField
          width={width}
          className={fieldClass}
          inline={inline || false}
        >
          <FieldLabel label={label} required={required} htmlFor={id} />
          <InputContainer
            inline={inline}
            {...containerProps(rest)}
            className={cn(inputContainerClass, rest.className)}
          >
            <PercentageMaskInput
              id={id}
              value={value}
              data-field-value={name}
              aria-invalid={!!error || undefined}
              onChange={next => onChange(next)}
              maskOptions={maskOptions}
              placeholder={placeholder || (isValidElement(label) ? '' : label)}
              {...rest}
              className={cn(!!error && FIELD_INPUT_INVALID, rest.className)}
            />
            {inputRight}
          </InputContainer>
        </CustomField>
      )}
    />
  );
}
